<template>
    <el-card class="customers" shadow="never">
        <template slot="header">
          <el-breadcrumb separator-class="el-icon-arrow-right">
            <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
            <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Customers' }">{{String($root.state.facilityData.code).toUpperCase()}} Members</el-breadcrumb-item>
            <el-breadcrumb-item v-else :to="{ name: 'Customers' }">Members</el-breadcrumb-item>
          </el-breadcrumb>
        </template>
        <ElButton v-if="$root.state.facility" @click="routeToCreateCustomer">
            Create Member
        </ElButton>
        <el-select v-if="$root.state.facility" v-model="selectedOption" placeholder="Select Option">
          <el-option label="All" value="all" />
          <el-option label="Active and Current Lease" value="activeWithoutDue" />
          <el-option label="Active and Past Due Lease" value="activeWithDue" />
          <el-option label="Active Lease" value="active" />
          <el-option label="Past Lease" value="inactive" />
          <el-option label="Pending Lease" value="pending" />
          <el-option label="Future Lease" value="future" />
          <el-option label="No lease" value="both" />
        </el-select>
        <ElTable
            :data="customers"
            style="width: 100%"
            @row-click="routeToCustomer"
            v-loading="loading"
            empty-text="No Data"
            @sort-change="fetchWithSort"
        >
            <ElTableColumn
                prop="company"
                sortable="companyName"
                label="Company Name">
            </ElTableColumn>
            <ElTableColumn
                prop="primaryContact.name"
                sortable="name"
                label="Name">
            </ElTableColumn>
            <ElTableColumn
                property="email"
                label="Email">
            </ElTableColumn>
            <ElTableColumn
                property="phone"
                label="Phone">
            </ElTableColumn>
            <ElTableColumn
                v-if="facility"
                property="due"
                sortable="due"
                :formatter="(row, column, cellValue ) => $options.filters.currencySign(cellValue, this.$root.state.facilityData)"
                label="Current Dues">
              </ElTableColumn>
              <ElTableColumn
                v-if="facility"
                sortable="ach"
                prop="ach"
                :formatter="(row, column, cellValue ) => $options.filters.currencySign(cellValue, this.$root.state.facilityData)"
                label="ACH Pending">
            </ElTableColumn>
        </ElTable>
        <el-dialog id="create-customer" :visible.sync="dialogFormVisible" destroy-on-close fullscreen>
          <CreateCustomer :facility="facility" @load="loadCustomers"/>
          <el-button v-if="finalStatus && activeStep > 2" @click="closeDialog" style="margin-top: 70px;">Finish</el-button>
        </el-dialog>
    </el-card>
</template>

<script>
import axios from 'axios';
import CreateCustomer from './CreateCustomerV2.vue';
import { CUSTOMER_API_BASE } from '../env';

export default {
  name: 'Customers',
  watch: {
    async facility() {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }

      this.selectedOption = null;
      if (!this.facility) {
        await this.getCustomers();
        return;
      }
      await this.getFacilityCustomers();
    },
    async selectedOption() {
      if (this.loading) {
        this.abortController.abort();
        this.abortController = new AbortController();
      }
      await this.getFacilityCustomers();
    },
  },
  props: {
    facility: { type: String },
  },
  components: { CreateCustomer },
  data() {
    return {
      customers: [],
      abortController: null,
      loading: true,
      sortBy: 'company',
      sorting: true,
      dialogFormVisible: false,
      finalStatus: false,
      activeStep: 0,
      selectedOption: 'active',
    };
  },
  async mounted() {
    this.abortController = new AbortController();
    if (this.facility) {
      await this.getFacilityCustomers();
      return;
    }
    await this.getCustomers();
  },
  methods: {
    async fetchWithSort({ column, prop, order }) {
      if (prop === 'due' || prop === 'ach') {
        if (order === 'descending') {
          this.customers.sort((item1, item2) => item2[prop === 'due' ? 'due' : 'ach'] - item1[prop === 'due' ? 'due' : 'ach']);
        } else {
          this.customers.sort((item1, item2) => item1[prop === 'due' ? 'due' : 'ach'] - item2[prop === 'due' ? 'due' : 'ach']);
        }
        return;
      }

      if (prop) {
        this.sortBy = prop;
        this.sorting = order === 'descending';
      }

      await this.getFacilityCustomers();
    },
    async loadCustomers(status, step) {
      this.finalStatus = status;
      this.activeStep = step;
      if (this.facility) {
        await this.getFacilityCustomers();
        return;
      }
      await this.getCustomers();
    },
    routeToCreateCustomer() {
      this.dialogFormVisible = true;
      // if (this.facility) {
      //   this.$router.push({ name: 'Facility Create Customer', params: { facility: this.facility } });
      //   return;
      // }
      // this.$router.push({ name: 'Create Customer' });
    },
    async getFacilityCustomers() {
      this.loading = true;
      try {
        const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v2/facility/${this.facility}`, {
          signal: this.abortController.signal,
          params: {
            type: this.selectedOption,
            ...(this.sortBy ? { sortBy: this.sortBy } : {}),
            ...(this.sorting ? { sorting: this.sorting } : {}),
          },
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        for (const customer of response.data) {
          const currentDate = new Date();
          const currentMonth = currentDate.getMonth() + 1;
          const currentYear = currentDate.getFullYear();
          customer.ach = customer.achTransactions && customer.achTransactions.length ? customer.achTransactions.map(transaction => transaction.amount).reduce((pv,nx) => pv + nx, 0) : 0;
          const due = customer.amountsByPeriod && customer.amountsByPeriod.length ? customer.amountsByPeriod.filter(item => item.year < currentYear || (item.year === currentYear && item.month <= currentMonth)).map(transaction => transaction.total).reduce((pv,nx) => pv + nx, 0) : 0;
          customer.due = due < 0 ? 0 : due;
        }

        this.customers = response.data;
      } catch (err) {
      }
      this.loading = false;
    },
    async getCustomers() {
      this.loading = true;
      try {
        const response = await axios.get(`${CUSTOMER_API_BASE}/customers/v1/get/all`, {
          signal: this.abortController.signal,
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        // eslint-disable-next-line no-console
        this.customers = response.data;
      } catch (err) {
      }
      this.loading = false;
    },
    routeToCustomer(row) {
      if (this.facility) {
        this.$router.push({ name: 'Facility Customer', params: { id: row._id, facility: this.facility } });
        return;
      }
      this.$router.push({ name: 'Customer', params: { id: row._id } });
    },
    closeDialog() {
      if (this.activeStep > 2) {
        this.dialogFormVisible = false;
      }
    },
  },
};
</script>

<style scoped>

</style>



// WEBPACK FOOTER //
// src/components/Customers.vue